import { ArrowRight, BookUser, Eye, Sparkles } from "lucide-react";
import { useState, type FC, type ReactNode } from "react";
import { useWatch } from "react-hook-form";

import {
  AddressFields,
  CheckboxField,
  CountryField,
  CountryMultiField,
  DateField,
  fieldClass,
  Form,
  formatYmd,
  FormRow,
  PhoneField,
  SelectField,
  StickyFormFooter,
  SwitchField,
  TextField
} from "@/components/form";
import { Surface } from "@/components/surface";
import { SettingsPage } from "@/components/templates";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { PageHeader, SectionHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useDashboardSummary } from "@/features/dashboard/useDashboardSummary";
import { cn } from "@/lib/utils";

import type { ForeignNational } from "../../../../api/types/foreignNational";
import { IMMIGRATION_STATUS_OPTION_GROUPS } from "../../../../constants/immigrationStatusOptions";
import { useForeignNationals } from "../../../foreignNationals/context/ForeignNationalsContext";
import { SourceTag } from "../../components/SourceTag";
import { formatDayLabel } from "../../lib/records";
import { useClientProfile } from "../../context/ClientProfileContext";
import { ReadinessMeter, useReadiness } from "../overview/ReadinessCard";
import {
  LANGUAGE_GROUPS,
  MARITAL_STATUS_OPTIONS,
  PERSONAL_SECTION_IDS,
  PRONOUN_OPTIONS,
  SEX_OPTIONS,
  SOURCE_FIELD,
  TITLE_OPTIONS,
  type PersonalValues
} from "./personalModel";
import { AutofillSheet } from "./AutofillSheet";
import { usePersonalForm, type PersonalForm } from "./usePersonalForm";

const STATUS_GROUPS = IMMIGRATION_STATUS_OPTION_GROUPS.map((g) => ({
  label: g.label,
  options: g.options.map((o) => ({ value: o.value, label: o.label ?? o.value }))
}));

/** Stacked field labels move to the row's label column; screen readers keep them. */
const HIDE_FIELD_LABELS =
  "[&_[data-slot=field][data-orientation=vertical]>[data-slot=field-label]]:sr-only";

const JumpButton: FC<{ label: string; onClick: () => void }> = ({
  label,
  onClick
}) => (
  <Button type="button" variant="ghost" size="xs" onClick={onClick}>
    {label}
    <ArrowRight />
  </Button>
);

/** One SettingsPage form section: a title above a single Surface of rows. */
const Section: FC<{
  id: string;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}> = ({ id, title, action, children }) => (
  <section id={id} className="flex scroll-mt-4 flex-col gap-3">
    <SectionHeader title={title} actions={action} />
    <Surface className="divide-y divide-line-1">{children}</Surface>
  </section>
);

/** A label-left row: the label column, then one or more fields. */
const Row: FC<{
  label: ReactNode;
  /** Keep the fields' own labels (address blocks with several parts). */
  keepLabels?: boolean;
  id?: string;
  children: ReactNode;
}> = ({ label, keepLabels = false, id, children }) => (
  <div
    id={id}
    className={cn(
      "grid scroll-mt-4 gap-x-4 gap-y-1.5 px-4 py-2.5 sm:grid-cols-[176px_minmax(0,1fr)]",
      !keepLabels && HIDE_FIELD_LABELS
    )}
  >
    <div className="pt-1.5 text-base text-fg-2">{label}</div>
    <div className="flex min-w-0 flex-col gap-3">{children}</div>
  </div>
);

const PersonalSkeleton: FC = () => (
  <div className="flex flex-col gap-8" aria-hidden>
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="flex flex-col gap-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    ))}
  </div>
);

type Sources = ForeignNational["field_sources"];

/** A row label with the source tag of the first sourced field ("Passport", "Intake"). */
const sourced = (
  text: string,
  fields: (keyof PersonalValues)[],
  sources: Sources
): ReactNode => {
  const source = fields
    .map((f) => SOURCE_FIELD[f])
    .map((key) => (key ? sources?.[key] : undefined))
    .find(Boolean);
  if (!source) return text;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {text}
      <SourceTag source={source} />
    </span>
  );
};

const SsnField: FC<{ form: PersonalForm }> = ({ form }) => {
  const { ssnMasked, ssnRevealed, revealSsn, isRevealingSsn } = form;
  const [editing, setEditing] = useState(false);
  const typing = editing || ssnRevealed || !ssnMasked;
  return (
    <div className="flex min-w-0 flex-1 items-end gap-1.5">
      {typing ? (
        <TextField<PersonalValues>
          name="ssn"
          label="Social Security Number"
          placeholder="123-45-6789"
          autoComplete="off"
          inputMode="numeric"
          className="min-w-0 flex-1"
        />
      ) : (
        <Field className={cn(fieldClass, "min-w-0 flex-1")}>
          <FieldLabel htmlFor="personal-ssn-masked">
            Social Security Number
          </FieldLabel>
          <Input
            id="personal-ssn-masked"
            value={ssnMasked ?? ""}
            readOnly
            className="font-mono tracking-wider"
          />
        </Field>
      )}
      {ssnMasked && !ssnRevealed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="secondary"
              onClick={() => revealSsn().catch(() => undefined)}
              disabled={isRevealingSsn}
            >
              <Eye />
              Show
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            Showing the full number is recorded in the client's activity.
          </TooltipContent>
        </Tooltip>
      ) : null}
      {ssnMasked && !typing ? (
        <Button type="button" variant="ghost" onClick={() => setEditing(true)}>
          Replace
        </Button>
      ) : null}
    </div>
  );
};

const PassportSummary: FC<{
  client: ForeignNational | null;
  onOpen: () => void;
}> = ({ client, onOpen }) => {
  const number = client?.passport_number?.trim();
  const country = client?.passport_country?.trim();
  const exp = client?.passport_expiration;
  const has = Boolean(number || exp);
  return (
    <div className="flex min-h-7 flex-wrap items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2 text-base">
        <BookUser className="size-3.5 shrink-0 text-fg-3" aria-hidden />
        {has ? (
          <span className="truncate">
            <span className="font-medium">{number || "Passport"}</span>
            {country ? ` · ${country}` : ""}
            {exp ? ` · expires ${formatDayLabel(exp)}` : ""}
          </span>
        ) : (
          <span className="text-fg-3">No passport on file</span>
        )}
      </div>
      <JumpButton
        label={has ? "All Passports" : "Add Passport"}
        onClick={onOpen}
      />
    </div>
  );
};

const PersonalFormBody: FC<{
  form: PersonalForm;
  client: ForeignNational | null;
  onOpenImmigration: () => void;
  onOpenAddresses: () => void;
  onOpenPassports: () => void;
}> = ({
  form,
  client,
  onOpenImmigration,
  onOpenAddresses,
  onOpenPassports
}) => {
  const { control } = form.form;
  const mailingSameAsPhysical = useWatch<PersonalValues>({
    control,
    name: "mailingSameAsPhysical"
  }) as boolean;
  const durationOfStatus = useWatch<PersonalValues>({
    control,
    name: "durationOfStatus"
  }) as boolean;
  const sources = client?.field_sources;
  const L = (text: string, ...fields: (keyof PersonalValues)[]) =>
    sourced(text, fields, sources);

  return (
    <>
      <Section
        id={PERSONAL_SECTION_IDS.biographical}
        title="Biographical Information"
      >
        <Row label={L("Legal Name", "firstName", "middleName", "lastName")}>
          <FormRow collapseBelow="sm">
            <TextField<PersonalValues>
              name="firstName"
              label="First Name"
              placeholder="Enter first name"
              autoComplete="given-name"
            />
            <TextField<PersonalValues>
              name="middleName"
              label="Middle Name"
              placeholder="Enter middle name"
              autoComplete="additional-name"
            />
            <TextField<PersonalValues>
              name="lastName"
              label="Last Name"
              placeholder="Enter last name"
              autoComplete="family-name"
            />
          </FormRow>
        </Row>
        <Row label={L("Other Names Used", "otherNames")}>
          <TextField<PersonalValues>
            name="otherNames"
            label="Other Names Used"
            placeholder="Maiden name, aliases, other spellings"
            description="Separate names with commas."
          />
        </Row>
        <Row label={L("Name in Native Script", "nativeName")}>
          <TextField<PersonalValues>
            name="nativeName"
            label="Name in Native Script"
            placeholder="e.g. 李伟, Аня Иванова"
          />
        </Row>
        <Row label={L("Title, Pronouns, Sex", "sex")}>
          <FormRow collapseBelow="sm">
            <SelectField<PersonalValues>
              name="title"
              label="Title"
              placeholder="Title"
              options={TITLE_OPTIONS}
              clearable
            />
            <SelectField<PersonalValues>
              name="pronouns"
              label="Pronouns"
              placeholder="Pronouns"
              options={PRONOUN_OPTIONS}
              clearable
            />
            <SelectField<PersonalValues>
              name="sex"
              label="Sex"
              placeholder="Sex"
              options={SEX_OPTIONS}
              clearable
            />
          </FormRow>
        </Row>
        <Row label={L("Date of Birth", "birthday")}>
          <DateField<PersonalValues>
            name="birthday"
            label="Date of Birth"
            max={formatYmd(new Date())}
            className="sm:max-w-50"
          />
        </Row>
        <Row label={L("Marital Status", "maritalStatus")}>
          <SelectField<PersonalValues>
            name="maritalStatus"
            label="Marital Status"
            options={MARITAL_STATUS_OPTIONS}
            clearable
            className="sm:max-w-50"
          />
        </Row>
        <Row
          label={L(
            "Place of Birth",
            "cityOfBirth",
            "stateOfBirth",
            "countryOfBirth"
          )}
        >
          <FormRow collapseBelow="sm">
            <TextField<PersonalValues>
              name="cityOfBirth"
              label="City of Birth"
              placeholder="City or town"
            />
            <TextField<PersonalValues>
              name="stateOfBirth"
              label="State or Province of Birth"
              placeholder="State or province"
            />
            <CountryField<PersonalValues>
              name="countryOfBirth"
              label="Country of Birth"
            />
          </FormRow>
        </Row>
        <Row label={L("Citizenship", "citizenships")}>
          <CountryMultiField<PersonalValues>
            name="citizenships"
            label="Citizenship"
            firstLabel="Primary"
            addLabel="Add Citizenship"
            description="List every country the client is a citizen of. The first is used where a form asks for one."
          />
        </Row>
      </Section>

      <Section id={PERSONAL_SECTION_IDS.contact} title="Contact and Language">
        <Row label="Email Address">
          <TextField<PersonalValues>
            name="email"
            label="Email Address"
            type="email"
            placeholder="Enter email address"
            autoComplete="off"
            description="Also the client's portal sign-in. Leave blank for a minor or dependent without portal access."
          />
        </Row>
        <Row label="Phone Number">
          <PhoneField<PersonalValues> name="phone" label="Phone Number" />
        </Row>
        <Row label="Secondary Email">
          <TextField<PersonalValues>
            name="secondaryEmail"
            label="Secondary Email"
            type="email"
            placeholder="Enter secondary email address"
            autoComplete="off"
          />
        </Row>
        <Row label="Secondary Phone">
          <PhoneField<PersonalValues>
            name="secondaryPhone"
            label="Secondary Phone"
          />
        </Row>
        <Row label="Preferred Language">
          <SelectField<PersonalValues>
            name="preferredLanguage"
            label="Preferred Language"
            groups={LANGUAGE_GROUPS}
            placeholder="English"
            clearable
            description="The portal, intake and emails use it when available."
          />
        </Row>
        <Row label="Interpreter">
          <SwitchField<PersonalValues>
            name="interpreterNeeded"
            label="Needs an interpreter"
            description="For interviews, biometrics and calls."
          />
        </Row>
      </Section>

      <Section
        id={PERSONAL_SECTION_IDS.identifiers}
        title="Identification Numbers"
      >
        <Row label={L("A-Number", "aNumber")}>
          <TextField<PersonalValues>
            name="aNumber"
            label="A-Number"
            placeholder="A012345678"
            autoComplete="off"
          />
        </Row>
        <Row label={L("USCIS Online Account", "uscisAccountNumber")}>
          <TextField<PersonalValues>
            name="uscisAccountNumber"
            label="USCIS Online Account Number"
            placeholder="12 digits"
            autoComplete="off"
            inputMode="numeric"
          />
        </Row>
        <Row label="Social Security Number">
          <SsnField form={form} />
        </Row>
        <Row label="Passport">
          <PassportSummary client={client} onOpen={onOpenPassports} />
        </Row>
      </Section>

      <Section
        id={PERSONAL_SECTION_IDS.immigration}
        title="Immigration Information"
        action={
          <JumpButton
            label="Edit Full Immigration History"
            onClick={onOpenImmigration}
          />
        }
      >
        <Row label={L("Current Status", "currentImmigrationStatus")}>
          <SelectField<PersonalValues>
            name="currentImmigrationStatus"
            label="Current Immigration Status"
            groups={STATUS_GROUPS}
            clearable
          />
        </Row>
        <Row label={L("Status Expires (I-94)", "immigrationStatusExpiration")}>
          <DateField<PersonalValues>
            name="immigrationStatusExpiration"
            label="Status Expires (I-94)"
            disabled={durationOfStatus}
            placeholder={durationOfStatus ? "D/S" : "Pick a date"}
            className="sm:max-w-50"
          />
          <CheckboxField<PersonalValues>
            name="durationOfStatus"
            label="Admitted for duration of status (D/S)"
            description="F, J and some other statuses have no fixed end date. Track the program end date below instead."
          />
        </Row>
        <Row label={L("Priority Date", "priorityDate")}>
          <DateField<PersonalValues>
            name="priorityDate"
            label="Priority Date"
            className="sm:max-w-50"
          />
        </Row>
        <Row label="Preference Category">
          <TextField<PersonalValues>
            name="preferenceCategory"
            label="Preference Category"
            placeholder="e.g. EB-2, F2A"
          />
        </Row>
        <Row
          id={PERSONAL_SECTION_IDS.expirations}
          label={L("EAD Card", "eadNumber", "eadExpiration")}
        >
          <FormRow collapseBelow="sm">
            <TextField<PersonalValues>
              name="eadNumber"
              label="EAD Card Number"
              placeholder="e.g. IOE0123456789"
              autoComplete="off"
            />
            <DateField<PersonalValues>
              name="eadExpiration"
              label="EAD Expires"
              placeholder="Expires"
            />
          </FormRow>
        </Row>
        <Row label={L("Visa Foil", "visaNumber", "visaExpiration")}>
          <FormRow collapseBelow="sm">
            <TextField<PersonalValues>
              name="visaNumber"
              label="Visa Foil Number"
              placeholder="Red number on the visa"
              autoComplete="off"
            />
            <DateField<PersonalValues>
              name="visaExpiration"
              label="Visa Expires"
              placeholder="Expires"
            />
          </FormRow>
        </Row>
        <Row label={L("Advance Parole Expires", "advanceParoleExpiration")}>
          <DateField<PersonalValues>
            name="advanceParoleExpiration"
            label="Advance Parole Expires"
            className="sm:max-w-50"
          />
        </Row>
        <Row label={L("Green Card Expires", "greenCardExpiration")}>
          <DateField<PersonalValues>
            name="greenCardExpiration"
            label="Green Card Expires"
            className="sm:max-w-50"
          />
        </Row>
        <Row label={L("Program End (I-20 / DS-2019)", "programEndDate")}>
          <DateField<PersonalValues>
            name="programEndDate"
            label="Program End (I-20 / DS-2019)"
            className="sm:max-w-50"
          />
        </Row>
        <Row label={L("H-1B Max-Out Date", "h1bMaxOutDate")}>
          <DateField<PersonalValues>
            name="h1bMaxOutDate"
            label="H-1B Max-Out Date"
            className="sm:max-w-50"
          />
        </Row>
      </Section>

      <Section
        id={PERSONAL_SECTION_IDS.address}
        title="Current Addresses"
        action={
          <JumpButton
            label="Edit Full Address History"
            onClick={onOpenAddresses}
          />
        }
      >
        <Row label="Physical Address" keepLabels>
          <AddressFields<PersonalValues> name="physical" />
        </Row>
        <Row label="Mailing Address" keepLabels>
          <Tabs
            value={mailingSameAsPhysical ? "same" : "different"}
            onValueChange={(v) => form.setMailingSameAsPhysical(v === "same")}
          >
            <TabsList aria-label="Mailing address mode">
              <TabsTrigger value="same">Same as Physical</TabsTrigger>
              <TabsTrigger value="different">Different</TabsTrigger>
            </TabsList>
          </Tabs>
          {mailingSameAsPhysical ? (
            <p className="text-sm text-fg-3">
              Mail goes to the physical address above.
            </p>
          ) : (
            <>
              <TextField<PersonalValues>
                name="inCareOf"
                label="In Care Of"
                placeholder="Name of the person mail is addressed to"
              />
              <AddressFields<PersonalValues> name="mailing" />
            </>
          )}
        </Row>
        <Row label="Address Abroad" keepLabels>
          <p className="text-sm text-fg-3">
            The client's address outside the U.S., asked for on the I-130,
            I-485, DS-260 and many visa forms.
          </p>
          <AddressFields<PersonalValues> name="foreign" />
        </Row>
      </Section>
    </>
  );
};

const joinAnd = (parts: string[]): string =>
  parts.length < 2
    ? parts.join("")
    : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;

/** "Birth date, city, country and citizenship missing", from the record itself. */
const missingSummary = (client: ForeignNational | null): string | null => {
  const birth = [
    !client?.birthday && "date",
    !client?.city_of_birth && "city",
    !client?.country_of_birth && "country"
  ].filter((p): p is string => Boolean(p));
  const citizenship =
    (client?.citizenships?.length ?? 0) > 0 ||
    Boolean(client?.country_of_citizenship);
  const parts = [
    birth.length ? `Birth ${birth.join(", ")}` : null,
    citizenship ? null : birth.length ? "citizenship" : "Citizenship"
  ].filter((p): p is string => Boolean(p));
  return parts.length ? `${joinAnd(parts)} missing` : null;
};

/** The Ready to File checks under the title, when there's an open case to measure against. */
const Completeness: FC<{ client: ForeignNational | null }> = ({ client }) => {
  const { clientId } = useClientProfile();
  const summary = useDashboardSummary({ kind: "client", id: clientId });
  const readiness = useReadiness(summary.data?.status_health);
  if (!readiness.hasOpenCase || !readiness.loaded) return null;
  const missing = missingSummary(client);
  return (
    <span
      className="flex min-w-0 items-center gap-2"
      data-testid="personal-completeness"
    >
      <ReadinessMeter pct={readiness.pct} className="w-16 shrink-0" />
      <span className="min-w-0 truncate">
        {readiness.done} of {readiness.checks.length} profile checks pass
        {missing ? ` · ${missing}` : ""}
      </span>
    </span>
  );
};

/** Personal Information: a SettingsPage column of label-left rows and a sticky save bar. */
export const PersonalTab: FC = () => {
  const { clientId, client, isLoadingClient, reloadClient, url } =
    useClientProfile();
  const { updateForeignNational } = useForeignNationals();
  const form = usePersonalForm(clientId, client, updateForeignNational);
  const ready = Boolean(client) && !isLoadingClient;
  const autofillOpen = url.state.focus.autofill === "1";
  const setAutofillOpen = (open: boolean) =>
    url.setTab("personal", open ? { autofill: "1" } : undefined);

  return (
    <Form
      form={form.form}
      onSubmit={form.submit}
      className="flex h-full min-h-0 w-full flex-col gap-0"
    >
      <SettingsPage className="max-w-204 pt-6">
        <PageHeader
          title="Personal Information"
          description={ready ? <Completeness client={client} /> : undefined}
          actions={
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={!ready || form.isDirty}
                    onClick={() => setAutofillOpen(true)}
                    data-testid="personal-autofill"
                  >
                    <Sparkles />
                    Autofill
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {form.isDirty
                  ? "Save or discard your changes first."
                  : "Fill the profile from the passport, I-94 and approval notices on this client's cases."}
              </TooltipContent>
            </Tooltip>
          }
        />
        {ready ? (
          <PersonalFormBody
            form={form}
            client={client}
            onOpenImmigration={() => url.setTab("immigration_overview")}
            onOpenAddresses={() => url.setTab("address_history")}
            onOpenPassports={() => url.setTab("passports")}
          />
        ) : (
          <PersonalSkeleton />
        )}
      </SettingsPage>
      <StickyFormFooter
        isDirty={form.isDirty}
        isSubmitting={form.isSaving}
        canSubmit={ready}
        onCancel={form.reset}
        submitLabel="Save Changes"
      />
      {client ? (
        <AutofillSheet
          open={autofillOpen && ready && !form.isDirty}
          onOpenChange={setAutofillOpen}
          clientId={clientId}
          client={client}
          onApplied={reloadClient}
        />
      ) : null}
    </Form>
  );
};
